import { useCallback, useMemo, useRef } from 'preact/hooks';
import type { ActiveFilters, FilterOption, FilterConfig } from '../hooks/useFilters';
import Combobox from './Combobox';
import SearchInput from './SearchInput';

type FilterBarProps = {
	filterConfigs: FilterConfig[];
	pending: Record< string, string[] >;
	setPendingFilter: ( key: string, values: string[] ) => void;
	applyFilters: () => void;
	activeFilters: ActiveFilters;
	hasActiveFilters: boolean;
	removeFilterValue: ( key: string, value: string ) => void;
	clearAllFilters: () => void;
	filterOptions: Record< string, FilterOption[] >;
	locations: MapLocation[];
	onSearchSelect: ( originalIndex: number ) => void;
	mapType: string;
};

/**
 * FilterBar component.
 * Renders combobox filters, submit button, and active filter tags.
 *
 * @param {Object} props - Component props (from useFilters + isMobile).
 * @return {*} jsx
 */
export default function FilterBar( {
	filterConfigs,
	pending,
	setPendingFilter,
	applyFilters,
	activeFilters,
	hasActiveFilters,
	removeFilterValue,
	clearAllFilters,
	filterOptions,
	locations,
	onSearchSelect,
	mapType,
}: FilterBarProps ) {
	// Ref for the first combobox trigger — focus target after clearing filters.
	const firstComboboxRef = useRef< HTMLDivElement >( null );

	// Build a map of location_type value → color from the locations data.
	const typeColorMap = useMemo( () => {
		const map: Record< string, string > = {};
		for ( const loc of locations ) {
			const locationType = loc.location_type;
			if ( locationType && loc.color && ! map[ locationType ] ) {
				map[ locationType ] = loc.color;
			}
		}
		return map;
	}, [ locations ] );

	/**
	 * Move focus to the first combobox trigger button.
	 */
	const focusFirstCombobox = useCallback( () => {
		requestAnimationFrame( () => {
			const trigger = firstComboboxRef.current?.querySelector< HTMLButtonElement >( '[role="combobox"]' );
			trigger?.focus();
		} );
	}, [] );

	const handleSubmit = useCallback(
		( e: Event ) => {
			e.preventDefault();
			applyFilters();
		},
		[ applyFilters ]
	);

	/**
	 * Clear all filters and return focus to the first combobox.
	 */
	const handleClearAll = useCallback( () => {
		clearAllFilters();
		focusFirstCombobox();
	}, [ clearAllFilters, focusFirstCombobox ] );

	/**
	 * Remove a single filter value. If it's the last active filter,
	 * move focus to the first combobox (since the tags container will unmount).
	 */
	const handleRemoveFilter = useCallback(
		( key: string, value: string ) => {
			const remainingCount = Object.values( activeFilters )
				.reduce( ( sum, values ) => sum + values.length, 0 ) - 1;

			removeFilterValue( key, value );

			if ( remainingCount <= 0 ) {
				focusFirstCombobox();
			}
		},
		[ activeFilters, removeFilterValue, focusFirstCombobox ]
	);

	return (
		<div className="str-maps__filters">
			<div className="str-maps__filters-row">
				<form className="str-maps__filters-form" onSubmit={ handleSubmit }>
					<div className="str-maps__filters-selects">
						{ filterConfigs.map( ( config, index ) => (
							<div
								key={ config.key }
								className="str-maps__filters-field"
								ref={ index === 0 ? firstComboboxRef : undefined }
							>
								<Combobox
									id={ config.id }
									label={ config.label }
									placeholder={ config.placeholder }
									options={ filterOptions[ config.key ] || [] }
									selected={ pending[ config.key ] || [] }
									onChange={ ( values ) => setPendingFilter( config.key, values ) }
								/>
							</div>
						) ) }

						<button
							type="submit"
							className="str-maps__filters-submit"
							aria-label="Apply filters"
						>
							<span className="str-maps__filters-submit-label">Apply Filters</span>
							<svg
								viewBox="0 0 20 20"
								fill="none"
								aria-hidden="true"
							>
								<path
									d="M4 10H16M16 10L11 5M16 10L11 15"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
					</div>
				</form>

				<SearchInput
					locations={ locations }
					onSelect={ onSearchSelect }
					mapType={ mapType }
				/>
			</div>

			{ hasActiveFilters && (
				<div className="str-maps__active-filters" role="status" aria-label="Active filters">
					{ filterConfigs.map( ( config ) =>
						( activeFilters[ config.key ] || [] ).map( ( value ) => {
							const tagColor = config.key === 'locationType' ? typeColorMap[ value ] : undefined;
							return (
								<span
									key={ `${ config.key }-${ value }` }
									className={ `str-maps__active-filter ${ tagColor ? 'has-custom-color' : '' }` }
									style={ tagColor ? { '--str-maps-location-color': tagColor } as Record< string, string > : undefined }
								>
									<span>{ value }</span>
									<button
										type="button"
										className="str-maps__active-filter-remove"
										aria-label={ `Remove ${ value } filter` }
										onClick={ () => handleRemoveFilter( config.key, value ) }
									>
										&times;
									</button>
								</span>
							);
						} )
					) }

					<button
						type="button"
						className="str-maps__active-filters-clear"
						onClick={ handleClearAll }
					>
						Clear All
					</button>
				</div>
			) }
		</div>
	);
}
